import { CssNode, parse } from "css-tree";

/** Capture-only internal prop. Callers must opt in and keep it out of DOM args. */
export const FORCED_VARIANTS_PROP = "__plasmicForcedVariants";

export function forcedClassName(uuid: string): string {
  let encoded = "";
  for (let i = 0; i < uuid.length; i++) {
    const character = uuid[i];
    encoded += /^[a-z0-9-]$/.test(character)
      ? character
      : `_${uuid.charCodeAt(i).toString(16)}_`;
  }
  return `plasmic-forced-${encoded}`;
}

export function forcedCheck(uuid: string): string {
  return `!!$props?.[${JSON.stringify(FORCED_VARIANTS_PROP)}]?.[${JSON.stringify(uuid)}]`;
}

export type SelectorRefusal =
  | "combinator"
  | "type-selector"
  | "list"
  | "nesting"
  | "unparsable"
  | "empty"
  | "non-standard-pseudo-class"
  | "unsupported-argument";
export type ForcibleSelector =
  | { ok: true; head: string; tail: string }
  | { ok: false; reason: SelectorRefusal };

const simplePseudoClasses = new Set([
  "hover",
  "active",
  "focus",
  "focus-visible",
  "focus-within",
  "visited",
  "link",
  "any-link",
  "target",
  "disabled",
  "enabled",
  "checked",
  "indeterminate",
  "required",
  "optional",
  "read-only",
  "read-write",
  "placeholder-shown",
  "default",
  "valid",
  "invalid",
  "in-range",
  "out-of-range",
  "empty",
  "first-child",
  "last-child",
  "only-child",
  "first-of-type",
  "last-of-type",
  "only-of-type",
]);
const selectorFunctions = new Set(["not", "is", "where", "has"]);
const nthFunctions = new Set([
  "nth-child",
  "nth-last-child",
  "nth-of-type",
  "nth-last-of-type",
]);
const legacyElements = new Set([
  "before",
  "after",
  "first-line",
  "first-letter",
]);
const pseudoElements = new Set([
  "before",
  "after",
  "marker",
  "first-line",
  "first-letter",
  "placeholder",
  "selection",
  "backdrop",
  "file-selector-button",
]);

function validateList(node: CssNode, insideHas: boolean): boolean {
  return (
    node.type === "SelectorList" &&
    !node.children.isEmpty &&
    node.children
      .toArray()
      .every(
        (selector) =>
          selector.type === "Selector" &&
          !selector.children.isEmpty &&
          selector.children
            .toArray()
            .every((child) => !validateHead(child, insideHas)),
      )
  );
}

function validateHead(
  node: CssNode,
  insideHas = false,
): SelectorRefusal | undefined {
  if (
    node.type === "ClassSelector" ||
    node.type === "IdSelector" ||
    node.type === "AttributeSelector"
  ) {
    return undefined;
  }
  if (node.type === "Combinator") {
    return "combinator";
  }
  if (node.type === "TypeSelector") {
    return "type-selector";
  }
  if (node.type === "NestingSelector") {
    return "nesting";
  }
  if (node.type !== "PseudoClassSelector") {
    return "unsupported-argument";
  }
  const name = node.name.toLowerCase();
  if (simplePseudoClasses.has(name)) {
    return node.children ? "unsupported-argument" : undefined;
  }
  if (selectorFunctions.has(name)) {
    if (name === "has" && insideHas) {
      return "unsupported-argument";
    }
    const children = node.children?.toArray();
    return children?.length === 1 &&
      validateList(children[0], insideHas || name === "has")
      ? undefined
      : "unsupported-argument";
  }
  if (nthFunctions.has(name)) {
    const children = node.children?.toArray();
    const nth = children?.length === 1 ? children[0] : undefined;
    return nth?.type === "Nth" &&
      (nth.nth.type === "AnPlusB" ||
        (nth.nth.type === "Identifier" &&
          /^(odd|even)$/i.test(nth.nth.name))) &&
      (!nth.selector ||
        ((name === "nth-child" || name === "nth-last-child") &&
          validateList(nth.selector, insideHas)))
      ? undefined
      : "unsupported-argument";
  }
  return "non-standard-pseudo-class";
}

/** css-tree tolerates EOF comments and empty trailing selector branches.
 * Reject those before wrapping in a forgiving :is(). Quoted attribute values
 * and escaped punctuation remain literals, never mistaken for syntax. */
function invalidSelectorTokens(selector: string): SelectorRefusal | undefined {
  let quote: string | undefined;
  let brackets = 0;
  let last: string | undefined;
  for (let i = 0; i < selector.length; i++) {
    const character = selector[i];
    if (character === "\\") {
      i++;
      last = "literal";
      continue;
    }
    if (quote) {
      if (character === quote) {
        quote = undefined;
      }
      continue;
    }
    if (character === '"' || character === "'") {
      quote = character;
      last = "literal";
      continue;
    }
    if (character === "/" && selector[i + 1] === "*") {
      return "unparsable";
    }
    if (character === "[") {
      brackets++;
    }
    if (character === "]") {
      brackets--;
    }
    if (/\s/.test(character)) {
      continue;
    }
    if (
      !brackets &&
      ((character === "," && (!last || last === "(" || last === ",")) ||
        (character === ")" && last === ","))
    ) {
      return "list";
    }
    last = character;
  }
  return last === "," ? "list" : undefined;
}

/** Parse a suffix that native codegen appends to its owner class. Never change
 * unsupported native selectors; only an explicitly selected variant refuses. */
export function splitForcibleSelector(selector: string): ForcibleSelector {
  if (!selector.trim()) {
    return { ok: false, reason: "empty" };
  }
  const invalid = invalidSelectorTokens(selector);
  if (invalid) {
    return { ok: false, reason: invalid };
  }
  let ast: CssNode;
  try {
    ast = parse(selector, { context: "selectorList", positions: true });
  } catch {
    return { ok: false, reason: "unparsable" };
  }
  if (ast.type !== "SelectorList" || ast.children.size !== 1) {
    return { ok: false, reason: "list" };
  }
  const compound = ast.children.first;
  if (compound?.type !== "Selector" || compound.children.isEmpty) {
    return { ok: false, reason: "empty" };
  }
  let tailOffset: number | undefined;
  for (const node of compound.children.toArray()) {
    const element =
      node.type === "PseudoElementSelector" ||
      (node.type === "PseudoClassSelector" &&
        legacyElements.has(node.name.toLowerCase()));
    if (element) {
      if (tailOffset !== undefined) {
        return { ok: false, reason: "unsupported-argument" };
      }
      if (
        !(
          node.type === "PseudoElementSelector" ||
          node.type === "PseudoClassSelector"
        ) ||
        !pseudoElements.has(node.name.toLowerCase()) ||
        node.children
      ) {
        return { ok: false, reason: "unsupported-argument" };
      }
      tailOffset ??= node.loc?.start.offset;
    } else {
      // State following an element tail would act on the pseudo-element itself;
      // this primitive only forces the originating native element's state.
      const reason =
        tailOffset !== undefined ? "unsupported-argument" : validateHead(node);
      if (reason) {
        return { ok: false, reason };
      }
    }
  }
  return {
    ok: true,
    head: selector.slice(0, tailOffset).trim(),
    tail: tailOffset === undefined ? "" : selector.slice(tailOffset),
  };
}

export function wrapForcibleSelector(
  selector: string,
  uuid: string,
): string | undefined {
  const split = splitForcibleSelector(selector);
  return split.ok && split.head
    ? `:is(${split.head}, :where(.${forcedClassName(uuid)}))${split.tail}`
    : undefined;
}

export type VariantForcePlan =
  | { kind: "wrap"; selectors: { head: string; tail: string }[] }
  | { kind: "checker-only" }
  | { kind: "pseudo-element-only" }
  | { kind: "unsupported"; reason: SelectorRefusal; selector?: string };

export function planVariantForce(
  selectors: string[],
  options: { hasChecker: boolean },
): VariantForcePlan {
  const wrapped: { head: string; tail: string }[] = [];
  for (const selector of selectors) {
    const split = splitForcibleSelector(selector);
    if (!split.ok) {
      return { kind: "unsupported", reason: split.reason, selector };
    }
    if (split.head) {
      wrapped.push({ head: split.head, tail: split.tail });
    }
  }
  if (wrapped.length) {
    return { kind: "wrap", selectors: wrapped };
  }
  if (options.hasChecker) {
    return { kind: "checker-only" };
  }
  return selectors.length
    ? { kind: "pseudo-element-only" }
    : { kind: "unsupported", reason: "empty" };
}
